import {
  OBJECT_FIT_VALUES,
  type BlockStyle,
  type ImageBlock,
  type ObjectFit,
} from '@blogforge/shared';
import type { DragPath } from '../../types';
import { useEditorStore } from '../../stores/editorStore';
import VariableInfusedTextEditor, {
  type RichTextEditorHandle,
} from '../tiptap/VariableInfusedTextEditor';
import { useRef } from 'react';
import { Switch } from '../ui/Switch';

interface ImageInspectorProps {
  block: ImageBlock;
  path: DragPath;

  variableNameOptions?: string[];
}

export function ImageInspector({ block, path, variableNameOptions = [] }: ImageInspectorProps) {
  const richTextRef = useRef<RichTextEditorHandle>(null);
  const updateBlock = useEditorStore((s) => s.updateBlock);
  const attrs = block.attrs;

  const set = (patch: Partial<ImageBlock['attrs']>) => {
    updateBlock(block.id, { attrs: { ...attrs, ...patch } }, path);
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="field-label">Image Url</label>
        <VariableInfusedTextEditor
          value={attrs.src}
          onChange={(val, srcIsMustache) => set({ src: val, srcIsMustache })}
          placeholder="Add a url... Use @ to reference a variable"
          options={variableNameOptions}
          ref={richTextRef}
          tagsOnly={true}
          maxTags={1}
        />
      </div>

      <div>
        <Switch
          checked={attrs.skipOptimize}
          label="Do not convert to webp"
          onChange={(checked) => set({ skipOptimize: checked })}
        />
      </div>

      <div>
        <label className="field-label" htmlFor="image-alt">
          Alt text
        </label>
        <input
          id="image-alt"
          type="text"
          className="field-input"
          placeholder="Describe the image"
          value={block.attrs?.alt ?? ''}
          onChange={(e) => set({ alt: e.target.value })}
        />
      </div>

      <div>
        <label className="field-label" htmlFor="image-caption">
          Caption
        </label>
        <input
          id="image-caption"
          type="text"
          className="field-input"
          placeholder="Image caption"
          value={block.attrs?.caption ?? ''}
          onChange={(e) => set({ caption: e.target.value })}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="field-label" htmlFor="image-width">
            Width (px)
          </label>
          <input
            id="image-width"
            type="number"
            min={1}
            className="field-input"
            placeholder="Auto"
            value={block.attrs?.width ?? ''}
            onChange={(e) => set({ width: e.target.value ? Number(e.target.value) : undefined })}
          />
        </div>

        <div>
          <label className="field-label" htmlFor="image-height">
            Height (px)
          </label>
          <input
            id="image-height"
            type="number"
            min={1}
            className="field-input"
            placeholder="Auto"
            value={block.attrs?.height ?? ''}
            onChange={(e) => set({ height: e.target.value ? Number(e.target.value) : undefined })}
          />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {(['maxHeight', 'maxWidth', 'minWidth', 'minHeight'] as (keyof BlockStyle)[]).map(
          (key, index) => (
            <div key={`max-min-${index}`}>
              <label className="field-label" htmlFor={key}>
                {key} (px)
              </label>

              <input
                id={key}
                type="number"
                min={0}
                className="field-input"
                placeholder="Auto"
                value={block.style?.[key] ? parseInt(block.style[key]) : ''}
                onChange={(e) =>
                  updateBlock(
                    block.id,
                    { style: { ...block.style, [key]: `${e.target.value}px` } },
                    path,
                  )
                }
              />
            </div>
          ),
        )}
      </div>

      <div className="flex gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={block.attrs?.fullWidth ?? false}
            onChange={(e) =>
              updateBlock(
                block.id,
                {
                  attrs: { ...block.attrs, fullWidth: e.target.checked },
                },
                path,
              )
            }
          />
          Full width
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={block.attrs?.fullHeight ?? false}
            onChange={(e) =>
              updateBlock(
                block.id,
                {
                  attrs: { ...block.attrs, fullHeight: e.target.checked },
                },
                path,
              )
            }
          />
          Full height
        </label>
      </div>

      <div>
        <label className="field-label" htmlFor="object-fit">
          Object fit
        </label>

        <select
          title="Select object fit"
          className="field-input"
          id="object-fit"
          value={block.style?.objectFit ?? 'none'}
          onChange={(e) =>
            updateBlock(
              block.id,
              {
                style: { ...block.style, objectFit: e.target.value as ObjectFit },
              },
              path,
            )
          }
        >
          {OBJECT_FIT_VALUES.map((op) => (
            <option value={op} key={op} className="capitalize">
              {op}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
